import { Checkbox } from '@bp/ui-components';
import classNames from 'classnames';
import { PersonLabelType } from 'components/Persons/types/personTypes';
import dayjs from 'dayjs';
import { LegacyRef, memo, ReactNode } from 'react';
import { getUserStatusAsIcon, getUserStatusTranslation } from 'utils/userStatusUtils';
import { PersonLabel } from '../PersonsLabelFilter/PersonLabel';
import styles from './PersonsListEntry.module.scss';

type PersonsListEntryProps = {
  personUuid: string;
  name: string | null;
  email: string | null;
  birthday: string | null;
  status: string;
  labels: PersonLabelType[];
  actions?: ReactNode;
  selected?: boolean;
  padding?: 's' | 'm';
  showBirthdays?: boolean;
  onSelectCheckbox?: (uuid: string) => void;
  onSelectRow?: (uuid: string) => void;
  onLabelClick: (label: PersonLabelType) => void;
  measureElementIndex?: number;
  measureElement?: LegacyRef<HTMLDivElement>;
};

export const PersonsListEntry = memo(
  ({
    personUuid,
    name,
    email,
    birthday,
    status,
    labels,
    actions,
    selected,
    padding = 's',
    showBirthdays,
    onSelectCheckbox,
    onSelectRow,
    onLabelClick,
    measureElementIndex,
    measureElement,
  }: PersonsListEntryProps) => {
    const statusText = getUserStatusTranslation(status);
    const statusIcon = getUserStatusAsIcon(status);

    const classes = classNames(styles['persons-list-entry'], styles[`padding-${padding}`], {
      [styles.selected]: selected,
      [styles['select-on-click']]: onSelectRow,
    });

    const showBirthday = showBirthdays && !!birthday;

    return (
      <div
        className={classes}
        onClick={
          onSelectRow
            ? (e) => {
                e.stopPropagation();
                e.preventDefault();
                onSelectRow(personUuid);
              }
            : undefined
        }
        ref={measureElement}
        data-index={measureElementIndex}
      >
        <div className={styles.icon} title={statusText}>
          {statusIcon}
        </div>
        <div className={styles.info}>
          <div className={styles.top}>
            {onSelectCheckbox || onSelectRow ? (
              <Checkbox
                className={styles.name}
                name={`select-${personUuid}`}
                label={name ?? ''}
                checked={selected}
                onClick={
                  onSelectCheckbox
                    ? () => onSelectCheckbox(personUuid)
                    : onSelectRow
                      ? (e) => {
                          e.stopPropagation();
                          onSelectRow(personUuid);
                        }
                      : undefined
                }
              />
            ) : (
              <div className={styles.name}>{name}</div>
            )}
            {(email || showBirthday) && (
              <div className={styles.data}>
                {email && <div className={styles.mail}>{email}</div>}
                {email && showBirthday && <div className={styles.divider}></div>}
                {showBirthday && <div className={styles.birthday}>{dayjs(birthday).format('DD.MM.YYYY')}</div>}
              </div>
            )}
          </div>
          {labels.length > 0 && (
            <div className={classNames(styles.labels, { [styles['no-top-margin']]: !email && !showBirthday })}>
              {labels.map((label) => {
                return <PersonLabel key={label.uuid} {...label} onClick={() => onLabelClick(label)} />;
              })}
            </div>
          )}
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.name === nextProps.name &&
      prevProps.email === nextProps.email &&
      prevProps.birthday === nextProps.birthday &&
      prevProps.status === nextProps.status &&
      prevProps.personUuid === nextProps.personUuid &&
      prevProps.selected === nextProps.selected &&
      prevProps.labels.length === nextProps.labels.length &&
      prevProps.labels.every(
        (label) => label === nextProps.labels.find((l) => l.source === label.source && l.value === label.value),
      )
    );
  },
);
